import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input, Table, message, Card, Typography } from "antd";
import { ping, searchJobs, getJobsPublic } from "../services/api";
import CareersNav from "../components/CareersNav";

const { Text } = Typography;
const PAGE_SIZE = 25;

// helper: add params to a base path; skip empty/null/"null"/"NULL" token
function buildUrlWithParams(basePath, { orgId, token, q, page, hash }) {
  const params = new URLSearchParams();
  if (orgId != null) params.set("orgId", String(orgId));
  if (q != null) params.set("q", String(q));
  if (page != null) params.set("page", String(page));

  // only include token if it's valid
  if (token && token !== "null" && token !== "NULL") {
    params.set("token", token);
  }

  const query = params.toString();
  return `${basePath}${query ? `?${query}` : ""}${hash || ""}`;
}

export default function Search() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const usp = useMemo(() => new URLSearchParams(search), [search]);

  // URL params
  const orgId = usp.get("orgId");
  const token = usp.get("token"); // may be empty/"null"; API layer will recover from hash/cookie
  const initialQ = usp.get("q") || "";
  const initialPage = Number(usp.get("page") || "1");

  // State
  const [q, setQ] = useState(initialQ);
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null);

  // Preserve the #token=... hash on all navigations
  const hash = typeof window !== "undefined" ? window.location.hash || "" : "";

  // Columns
  const columns = [
    { title: "Job ID", dataIndex: "job_req_id", width: 100 },
    { title: "Job Title", dataIndex: "job_req_title", width: 260 },
    { title: "Area of Interest", dataIndex: "area_of_interest", width: 220 },
    { title: "Job Location", dataIndex: "location", width: 240 },
  ];

  function normalizeLogoUrl(logoUrl) {
    if (!logoUrl) return "";
    let path = (logoUrl || "").trim();

    // If it's absolute (http/https), prefer the path if it points to /uploads/
    try {
      const u = new URL(path, window.location.origin);
      if (u.pathname.startsWith("/uploads/")) {
        return u.pathname; // -> "/uploads/...."
      }
      // If it's already same-origin, also return its path
      if (u.origin === window.location.origin) return u.pathname + u.search + u.hash;
      // Otherwise, fall back to returning the absolute URL untouched
      return u.href;
    } catch {
      // Not a full URL, ensure leading slash
      if (!path.startsWith("/")) path = "/" + path;
      return path;
    }
  }

  function isAuthError(err) {
    const msg = String(err?.message || err || "").toLowerCase();
    return (
      msg.includes("401") ||
      msg.includes("unauthorized") ||
      msg.includes("403") ||
      msg.includes("forbidden")
    );
  }

  // Validate token/org context (non-fatal if it fails)
  useEffect(() => {
    let active = true;
    // Do not attempt anything if orgId is missing — keep the page quiet.
    if (!orgId) return;

    (async () => {
      try {
        // Let the API layer supply the real token from hash/cookie; don't enforce the query param here
        const res = await ping({ orgId });
        if (!active) return;
        const orgData = res?.organization || res?.org || { organization_id: orgId };
        const fixedLogo = normalizeLogoUrl(orgData.logo_url);
        setOrg({ ...orgData, logo_url: fixedLogo }); // normalize here
      } catch (err) {
        // Quietly ignore ping failures; we'll still allow public search fallback.
        console.warn("ping failed, will use public endpoints only:", err?.message);
      }
    })();
    return () => {
      active = false;
    };
  }, [orgId, token]); // Added token to dependencies

  const runSearch = useCallback(
    async (params) => {
      if (!orgId) return; // hard guard: never fetch without orgId
      setLoading(true);
      try {
        // Always try the PRIVATE search first. Do NOT pass the raw token from the querystring;
        // the API layer will inject the correct token from #hash or cookie.
        try {
          const resp = await searchJobs({
            q: params.q,
            page: params.page,
            limit: PAGE_SIZE,
            orgId,
          });
          const items = resp?.items || resp?.rows || [];
          setRows(items);
          setTotal(Number(resp?.total || items?.length || 0));
          return; // success → stop here
        } catch (err) {
          if (!isAuthError(err)) {
            throw err; // real error → surface it below
          }
          // else auth problem → fall back to public
        }

        // PUBLIC fallback (no joined AOI/location if your public route doesn't include them)
        const pub = await getJobsPublic({
          orgId,
          q: params.q,
          page: params.page,
          pageSize: PAGE_SIZE,
        });

        const items = pub?.items || [];
        setRows(items);

        // Compute a reasonable total for AntD pagination
        const computedTotal = pub?.hasMore
          ? params.page * PAGE_SIZE + 1
          : (params.page - 1) * PAGE_SIZE + items.length;
        setTotal(computedTotal);
      } catch (err) {
        console.error(err);
        message.error("Failed to load jobs.");
      } finally {
        setLoading(false);
      }
    },
    [orgId]
  );

  // First load + whenever URL-driven q/page change
  useEffect(() => {
    setQ(initialQ);
    setPage(initialPage);
    if (orgId) {
      runSearch({ q: initialQ, page: initialPage });
    }
  }, [initialQ, initialPage, runSearch, orgId]);

  const onSearch = (value) => {
    const qv = value || "";
    // Require orgId to proceed; keep URL clean
    if (!orgId) {
      message.warning("Organization is required to search jobs.");
      return;
    }
    // Update URL (keep hash) + fetch
    navigate(
      buildUrlWithParams("/", {
        orgId,
        token, // harmless; will be omitted if invalid; API won't rely on it
        q: qv,
        page: 1,
        hash,
      })
    );
    setQ(qv);
    setPage(1);
    runSearch({ q: qv, page: 1 });
  };

  // If orgId is missing, block UI with a friendly message (no API calls).
  if (!orgId) {
    return (
      <>
        <CareersNav org={org} orgId={orgId} token={token} />
        <div className="page-wrap" style={{ paddingTop: 24, paddingBottom: 40 }}>
          <Card style={{ borderColor: "#eee" }}>
            <Text type="secondary">
              Error #707. Something is missing. Please check the link again and retry!
            </Text>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <CareersNav org={org} orgId={orgId} token={token} />

      <div className="page-wrap">
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 12px" }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Input.Search
              placeholder="Search job title, skills, keywords…"
              allowClear
              enterButton="Search"
              defaultValue={q}
              onSearch={onSearch}
              style={{ maxWidth: 520 }}
            />
          </div>

          <Table
            rowKey="job_req_id"
            size="middle"
            loading={loading}
            dataSource={rows}
            columns={columns}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              onChange: (p) => {
                setPage(p);
                // update URL (and keep hash)
                navigate(
                  buildUrlWithParams("/", {
                    orgId,
                    token,
                    q: q || "",
                    page: p,
                    hash,
                  })
                );
                // fetch data
                runSearch({ q, page: p });
              },
              showTotal: (t) => `${t} job${t === 1 ? "" : "s"}`,
            }}
            onRow={(record) => ({
              onClick: () =>
                navigate(
                  buildUrlWithParams(`/job/${record.job_req_id}`, {
                    orgId,
                    token,
                    q: q || "",
                    page,
                    hash,
                  })
                ),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      </div>
    </>
  );
}
